/**
 * Online/offline detection - combines `navigator.onLine` (cheap, but unreliable
 * on macOS / captive portals) with a periodic /api/health probe.
 *
 * State is exposed via a small zustand-style subscriber so any component or
 * hook can react to changes without polling. The cached state survives the
 * lifetime of the app (the store lives on the module).
 *
 * Behaviour:
 *   - On boot, optimistically assume online (so initial queries try).
 *   - When `navigator.onLine` flips, surface the change immediately.
 *   - Every 30s while believed-online, ping /api/health; if 2 consecutive
 *     pings fail, flip to offline. While offline, ping every 10s so we
 *     return to online quickly when connectivity is restored.
 *
 * The mutation guard in `api/client.ts` reads `getOnlineStatus().online`
 * before sending any non-GET request and short-circuits with an offline
 * error so the UI never produces a half-completed action.
 */

type Listener = (online: boolean) => void;

let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
let consecutiveFailures = 0;
const listeners = new Set<Listener>();
let probeTimer: ReturnType<typeof setTimeout> | null = null;

function notify() {
  for (const l of listeners) l(isOnline);
}

function setOnline(next: boolean) {
  if (next === isOnline) return;
  isOnline = next;
  notify();
}

async function probe(): Promise<void> {
  // We rely on the API client's resolved base URL - keep this aligned by
  // re-resolving here (cheap; only on the probe interval).
  try {
    const base = resolveHealthUrl();
    const ctrl = new AbortController();
    const tm = setTimeout(() => ctrl.abort(), 5000);
    const res = await fetch(base, { method: 'GET', signal: ctrl.signal });
    clearTimeout(tm);
    if (res.ok) {
      consecutiveFailures = 0;
      setOnline(true);
    } else {
      consecutiveFailures++;
      if (consecutiveFailures >= 2) setOnline(false);
    }
  } catch {
    consecutiveFailures++;
    if (consecutiveFailures >= 2) setOnline(false);
  } finally {
    scheduleNextProbe();
  }
}

function resolveHealthUrl(): string {
  const injected = (window as unknown as { tcCrm?: { apiBaseUrl?: string } }).tcCrm
    ?.apiBaseUrl;
  if (typeof injected === 'string' && injected.length > 0) {
    return `${injected.replace(/\/$/, '')}/health`;
  }
  if (window.location.protocol === 'file:') return 'http://127.0.0.1:54330/api/health';
  return '/api/health';
}

function scheduleNextProbe() {
  if (probeTimer) clearTimeout(probeTimer);
  probeTimer = setTimeout(() => void probe(), isOnline ? 30_000 : 10_000);
}

/** Initialise listeners and start the probe loop. Idempotent. */
let started = false;
export function startOnlineDetection(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  window.addEventListener('online', () => {
    setOnline(true);
    consecutiveFailures = 0;
  });
  window.addEventListener('offline', () => {
    setOnline(false);
  });

  scheduleNextProbe();
}

export function getOnlineStatus(): { online: boolean } {
  return { online: isOnline };
}

export function subscribeOnlineStatus(listener: Listener): () => void {
  listeners.add(listener);
  // Fire immediately so subscribers get the current value.
  listener(isOnline);
  return () => {
    listeners.delete(listener);
  };
}
